import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Logo from "../logo.png";
import { signupWithEmailAndPass, authWithGoogle } from "../api/auth";
import { initializeUserCredits } from "../api/businesses";
import { validateEmail } from "../api/auth";
const INVALID_EMAIL_ERROR_MESSAGE = "Invalid or undeliverable email";

const SignupPage: React.FC = () => {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    // Check if passwords match
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    try {
      // Validate email before proceeding with signup
      const isEmailValidResponse = await validateEmail(email);
      const isEmailValid = isEmailValidResponse.data.valid;
      if (!isEmailValid) {
        setError("Invalid or undeliverable email address");
        return;
      }

      await signupWithEmailAndPass(email, password, firstName, lastName);
      await initializeUserCredits(); // Initialize credits after signup
      navigate("/furnish-home"); // Redirect to the furnish-home
    } catch (error: any) {
      if (
        error.response &&
        error.response.data.message === INVALID_EMAIL_ERROR_MESSAGE
      ) {
        setError(
          "Invalid or undeliverable email address. Please enter a valid email address!"
        );
      } else {
        setError("Failed to sign up. Please try again.");
      }
    }
  };

  const handleGoogleSignup = async () => {
    try {
      await authWithGoogle();
      await initializeUserCredits(); // Initialize credits after signup
      navigate("/furnish-home"); // Redirect after successful signup
    } catch (err) {
      setError("Failed to sign up with Google.");
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-100">
      <div className="w-full max-w-md p-8 space-y-6 bg-white shadow-lg rounded-lg">
        {/* Logo */}
        <div className="flex justify-center mb-4">
          <img src={Logo} alt="Logo" className="w-32 h-32 object-contain" />
        </div>

        <h2 className="text-2xl font-bold text-center text-gray-800">
          Sign Up
        </h2>
        {error && <p className="text-red-500 text-center">{error}</p>}

        <form className="space-y-4" onSubmit={handleSignup}>
          {/* First and Last Name Fields */}
          <div className="flex gap-4">
            <div className="w-1/2">
              <label
                htmlFor="firstName"
                className="block text-sm font-medium text-gray-600"
              >
                First Name
              </label>
              <input
                type="text"
                id="firstName"
                className="w-full p-2 mt-1 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                required
              />
            </div>
            <div className="w-1/2">
              <label
                htmlFor="lastName"
                className="block text-sm font-medium text-gray-600"
              >
                Last Name
              </label>
              <input
                type="text"
                id="lastName"
                className="w-full p-2 mt-1 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                required
              />
            </div>
          </div>

          {/* Email Field */}
          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium text-gray-600"
            >
              Email
            </label>
            <input
              type="email"
              id="email"
              className="w-full p-2 mt-1 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>

          {/* Password Field */}
          <div>
            <label
              htmlFor="password"
              className="block text-sm font-medium text-gray-600"
            >
              Password
            </label>
            <input
              type="password"
              id="password"
              className="w-full p-2 mt-1 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>

          {/* Confirm Password Field */}
          <div>
            <label
              htmlFor="confirmPassword"
              className="block text-sm font-medium text-gray-600"
            >
              Confirm Password
            </label>
            <input
              type="password"
              id="confirmPassword"
              className="w-full p-2 mt-1 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
            />
          </div>

          {/* Sign Up Button */}
          <button
            type="submit"
            className="w-full py-2 text-white bg-blue-500 rounded-lg hover:bg-blue-600 transition duration-200"
          >
            Sign Up
          </button>
        </form>

        {/* Separator */}
        <div className="flex items-center my-4">
          <div className="flex-grow h-px bg-gray-300"></div>
          <span className="px-3 text-sm text-gray-500">Or continue with</span>
          <div className="flex-grow h-px bg-gray-300"></div>
        </div>

        {/* Google Signup Button */}
        <button
          onClick={handleGoogleSignup}
          className="w-full py-2 border border-gray-300 rounded-lg flex items-center justify-center hover:bg-gray-100 transition duration-200"
        >
          <img
            src="https://www.svgrepo.com/show/355037/google.svg"
            alt="Google Icon"
            className="w-5 h-5 mr-2"
          />
          <span className="text-gray-600">Sign up with Google</span>
        </button>

        {/* Link to Login Page */}
        <p className="text-sm text-center text-gray-600 mt-4">
          Already have an account?{" "}
          <Link to="/login" className="text-blue-500 hover:underline">
            Login
          </Link>
        </p>
      </div>
    </div>
  );
};

export default SignupPage;
